import { memo, useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import { Stack, Typography, styled } from '@mui/material';

import { useBasicStore } from 'store';
import { dLog } from '@/_helpers/renderDebug';
import { videoRendererType } from '@/_models/virtualStaging/VideoRenderer.component';
import { FAIcon } from '@packages/ui-components';

function formatDuration(value: number) {
  const minute = Math.floor(value / 60);
  const secondsLeft = Math.floor(value - minute * 60);
  return `${minute}:${secondsLeft < 10 ? `0${secondsLeft}` : secondsLeft}`;
}

const TinyText = styled(Typography)({
  fontSize: '0.75rem',
  fontWeight: 500,
  letterSpacing: 0.2,
});

const SoundControls = ({ audio, onClose, vsObjects, flowIsActive }: any) => {
  dLog('SoundControls');
  const [soundTime, setSoundTime] = useState(0);
  const [isShowVolumeRange, toggleVolumeRange] = useState(false);
  const audioConfigs = useBasicStore((state) => state.audioConfigs);
  const setAudioConfigs = useBasicStore((state) => state.setAudioConfigs);

  useEffect(() => {
    if (audio) {
      const updateSoundTime = () => {
        setSoundTime((audio.currentTime / audio.duration) * 100);
      };

      audio.addEventListener('timeupdate', updateSoundTime);
      audio.addEventListener('ended', () => {
        setSoundTime(0);
      });

      return () => {
        audio.removeEventListener('timeupdate', updateSoundTime);
      };
    }
  }, []);

  const setTogglePlay = useCallback(() => {
    if (audio) {
      if (audio.paused) {
        const vsVideos = vsObjects?.filter((obj) => obj.node?.componentType === videoRendererType);
        vsVideos?.forEach((obj) => {
          const { video: vsVideo, muteBtn } = obj.node;
          if (!muteBtn.active) {
            vsVideo.muted = true;
            muteBtn.active = true;
            muteBtn.makeBtn(muteBtn.inputs.src);
          }
        });
        audio.play();
      } else {
        audio.pause();
      }
    }
  }, [vsObjects]);

  const handleClose = useCallback(
    (mode: 'close' | 'hide') => () => {
      if (audio) {
        if (mode === 'close') {
          audio.pause();
          if (audio?.closeReceivers) {
            audio?.closeReceivers?.dispatchEvent(new Event('close'));
          }
        }
        if (onClose && typeof onClose === 'function') {
          onClose();
        }
      }
    },
    [onClose],
  );

  return (
    <div className="sounds" style={{ bottom: flowIsActive ? '260px' : '90px' }}>
      <div className="container-controls">
        <FAIcon icon="faMinus" onClick={handleClose('hide')} />
        <FAIcon icon="faTimes" onClick={handleClose('close')} />
      </div>
      <div className="sound-range">
        <input
          type="range"
          step="0.1"
          min="0"
          max="100"
          value={soundTime}
          onChange={(data) => {
            if (audio) {
              audio.currentTime = (parseFloat(data.target.value) * audio.duration) / 100;
            }
          }}
        />
      </div>
      <div className="sound-controls">
        <div className="left-side">
          <div className="play"></div>
          <i
            className={classnames('fas', audio?.paused ? 'fa-play' : 'fa-pause')}
            onClick={setTogglePlay}
          />
        </div>
        {audio?.textTracks?.length ? (
          <div>
            <FAIcon
              icon="faClosedCaptioning"
              className={classnames({
                'enabled': audioConfigs.captionsEnabled
              })}
              onClick={() =>
                setAudioConfigs({ ...audioConfigs, captionsEnabled: !audioConfigs.captionsEnabled })
              }
            />
          </div>
        ) : null}
        <div className="volume">
          <FAIcon icon="faVolumeUp" onClick={() => toggleVolumeRange(!isShowVolumeRange)} />
          {isShowVolumeRange && (
            <input
              type="range"
              step="0.1"
              min="0"
              max="1"
              value={audio?.volume || 1}
              onChange={(data) => {
                if (audio) {
                  audio.volume = parseFloat(data.target.value);
                }
              }}
            />
          )}
        </div>
      </div>
      <Stack direction="row" spacing={0.5} sx={{ color: '#fff' }}>
        <TinyText>{`${formatDuration(audio?.currentTime || 0)}`}</TinyText>
        <TinyText>/</TinyText>
        <TinyText>{formatDuration(audio?.duration || 0)}</TinyText>
      </Stack>
    </div>
  );
};

export default memo(SoundControls);
